
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnsType } from "antd/es/table";

import { Price } from "@/types/sales";
import SText from "@/styles/SText";
import dayjs from "dayjs";
import { dateFormat, priceFormat } from "@/utils/format";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { CreditLevelSelect, CurrencySelect, CountrySelect } from "@/components/Selector";
import { Flex } from "antd";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";

type UsePriceColumn = () => ColumnsType<Price>

const usePriceColumn: UsePriceColumn = () => {
  const { t } = useTranslation();

  const columns: ColumnsType<Price> = useMemo(() => [
    {
      title: <SText text={t("product.code")} />,
      key: "code",
      width: 50,
      fixed: "left",
      render: (_, item) => (
        <SText key={item.id + "code"} text={item.product?.code ?? ""} />
      ),
    },
    {
      title: <SText text={t("product.name")} />,
      key: "name",
      width: 50,
      fixed: "left",
      render: (_, item) => (
        <SText key={item.id + "name"} text={item.product?.name ?? ""} />
      ),
    },
    {
      title: <SText text={t("sale.taxPrice")} />,
      key: "price",
      width: 30,
      render: (_, item) => <SText text={priceFormat(item.price ?? "")} />,
    },
    {
      title: <SText text={t("sale.estimateDays")} />,
      key: "estimateDays",
      width: 60,
      render: (_, item) => (
        <SText key={item.id + "estimateDays"} text={item.estimateDays?.toString() ?? ""} />
      ),
    },
    {
      title: <SText text={t("sale.expiredAt")} />,
      key: "expiredAt",
      width: 140,
      render: (_, item) => (
        <SText key={item.id + "expiredAt"} text={item.expiredAt ? dateFormat(dayjs(item.expiredAt)) : ''} />
      ),
    },
    {
      title: <SText text={t("common.desc")} />,
      key: "desc",
      width: 100,
      render: (_, item) => <SText text={item.desc ?? ""} />,
    },
    {
      title: <SText text={t("product.brand1")} />,
      key: "model1",
      width: 130,
      render: (_, item) => <SText text={item.product?.model?.toLocaleUpperCase() ?? ""} />,
    },
    {
      title: <SText text={t("product.brand2")} />,
      key: "model2",
      width: 130,
      render: (_, item) => <SText text={item.product?.t1?.toLocaleUpperCase() ?? ""} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      key: "uom",
      width: 30,
      render: (_, item) => <SText text={item.product?.uom?.toLocaleUpperCase() ?? ""} />,
    },
    {
      title: <SText text={t("sale.code")} />,
      key: "code",
      width: 30,
      render: (_, item) => <SText text={item.customer?.code ?? ""} />,
    },
    {
      title: <SText text={t("sale.name1")} />,
      key: "name1",
      width: 80,
      render: (_, item) => <SText text={item.customer?.name1 ?? ""} />,
    },
    {
      title: <SText text={t("sale.name2")} />,
      key: "name2",
      width: 80,
      render: (_, item) => <SText text={item.customer?.name2 ?? ""} />,
    },
    {
      title: <SText text={t("sale.shortName")} />,
      key: "shortName",
      width: 30,
      render: (_, item) => <SText text={item.customer?.shortName ?? ""} />,
    },
    {
      title: <SText text={t("sale.country")} />,
      key: "country",
      width: 40,
      render: (val, item) => <CountrySelect key={item.id} value={item.customer?.country ?? ''} readOnly={true} />,
    },
    {
      title: <SText text={t("sale.address")} />,
      key: "address",
      width: 100,
      render: (_, item) => <SText text={item.customer?.address ?? ""} />,
    },
    {
      title: <SText text={t("sale.zipCode")} />,
      key: "zipCode",
      width: 40,
      render: (_, item) => <SText text={item.customer?.zipCode ?? ""} />,
    },
    {
      title: <SText text={t("sale.email")} />,
      key: "email",
      width: 60,
      render: (_, item) => <SText text={item.customer?.email ?? ""} />,
    },
    {
      title: <SText text={t("sale.contact")} />,
      key: "contact",
      width: 40,
      render: (_, item) => <SText text={item.customer?.contact ?? ""} />,
    },
    {
      title: <SText text={t("sale.phone")} />,
      key: "phone",
      width: 50,
      render: (_, item) => <SText text={item.customer?.phone ?? ""} />,
    },
    {
      title: <SText text={t("sale.fax")} />,
      key: "fax",
      width: 50,
      render: (_, item) => <SText text={item.customer?.fax ?? ""} />,
    },
    {
      title: <SText text={t("sale.currency")} />,
      dataIndex: "currency",
      width: 40,
      render: (val, item) => (
        <CurrencySelect key={item.customer?.id} value={item.customer?.currency || ""} readOnly={true} />
      ),
    },
    {
      title: <SText text={t("sale.saleType")} />,
      dataIndex: "saleType",
      width: 60,
      render: (val, item) => (
        <Dict type={DictEnum.SALE_TYPE} value={item.customer?.saleType} readOnly />
      ),
    },
    {
      title: <SText text={t("sale.creditLevel")} />,
      dataIndex: "creditLevel",
      width: 30,
      render: (val, item) => (
        <CreditLevelSelect value={item.customer?.creditLevel} readOnly key={item.id + "creditLevel"} />
      ),
    },
    {
      title: <SText text={t("sale.creditLine")} />,
      key: "creditLine",
      width: 40,
      render: (_, item) => <SText text={item.customer?.creditLine ?? ""} />,
    },
    {
      title: <SText text={t("sale.depositRate")} />,
      key: "depositRate",
      width: 40,
      render: (_, item) => (
        <SText text={item.customer?.depositRate ?? ""} suffix="%" />
      ),
    },
    {
      title: <SText text={t("sale.tradeTerm")} />,
      key: "tradeTerm",
      width: 40,
      render: (_, item) => (
        <Dict type={DictEnum.SALE_TRADE_TERM} value={item.customer?.tradeTerm} readOnly />
      ),
    },
    {
      title: <SText text={t("sale.payMethod")} />,
      key: "payMethod",
      width: 60,
      render: (_, item) => (
        <Dict type={DictEnum.SALE_PAY_METHOD} value={item.customer?.payMethod} readOnly />
      ),
    },
    {
      title: <SText text={t("common.creator")} />,
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
  ], [t]);

  return columns;
}

export default usePriceColumn;